import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IModule, IMasterclass, IDeliverableAnswer, IGeneratedDeliverable, IUser } from '@shared';
import { FirebaseService } from 'src/app/firebase/firebase.service';
import { MasterclassService } from '../masterclasses/masterclass.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { SubSink } from 'subsink';

interface ModuleStatus {
  module: IModule;
  isAnswered: boolean;
  chapterId?: string;
  courseId?: string;
}

@Component({
  selector: 'app-deliverable-recap',
  templateUrl: './deliverable-recap.component.html',
  styleUrls: ['./deliverable-recap.component.scss']
})
export class DeliverableRecapComponent implements OnInit, OnDestroy {
  masterclass: IMasterclass | null = null;
  masterclassId: string;
  moduleStatuses: ModuleStatus[] = [];
  deliverableAnswers: IDeliverableAnswer[] = [];
  generatedDeliverable: IGeneratedDeliverable | null = null;
  currentUser: IUser;

  selectedImageStyle: string = 'photorealistic';
  imageStyles = [
    { value: 'photorealistic', label: 'Photoréaliste' },
    { value: 'illustrative', label: 'Illustratif' },
    { value: 'abstract', label: 'Abstrait' }
  ];

  isGenerating: boolean = false;
  isAllAnswered: boolean = false;
  loading: boolean = true;
  error: string = '';
  linkCopied: boolean = false;

  subs = new SubSink();
  private pollingInterval: any = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private firebaseService: FirebaseService,
    private masterclassService: MasterclassService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.subs.unsubscribe();
  }

  loadData(): void {
    this.masterclassId = this.route.snapshot.paramMap.get('id');

    this.firebaseService.getCurrentUserProperties().subscribe((user) => {
      this.currentUser = user;

      this.firebaseService.getRemoteConfig().subscribe((remoteConfig) => {
        const versionId = remoteConfig.parameters['selectedELearningVersion'].defaultValue.value;

        this.masterclassService.getMasterclassVersion(versionId).subscribe((version) => {
          this.masterclass = version.masterclasses.find(m => m.id === this.masterclassId) || version.masterclasses[0];

          this.firebaseService.getELearningData(true).subscribe((data) => {
            this.deliverableAnswers = data?.deliverableAnswers || [];
            const deliverables = data?.generatedDeliverables || [];
            const foundDeliverable = deliverables.find(d => d.masterclassId === this.masterclassId);

            if (foundDeliverable) {
              const rawDate = foundDeliverable.generatedAt as any;
              this.generatedDeliverable = {
                ...foundDeliverable,
                generatedAt: rawDate instanceof Date
                  ? rawDate
                  : rawDate?.toDate
                    ? rawDate.toDate()
                    : new Date(rawDate)
              };
            } else {
              this.generatedDeliverable = null;
            }

            this.buildModuleStatuses();
            this.loading = false;

            if (this.generatedDeliverable?.status === 'pending') {
              this.startPolling();
            }
          });
        });
      });
    });
  }

  buildModuleStatuses(): void {
    if (!this.masterclass) return;

    this.moduleStatuses = this.masterclass.modules.map((module) => {
      let hasDeliverableQuestions = false;
      let deliverableChapterId: string | undefined;
      let deliverableCourseId: string | undefined;

      for (const chapter of module.chapters) {
        for (const course of chapter.courses) {
          if (course.deliverableQuestions?.length) {
            hasDeliverableQuestions = true;
            deliverableChapterId = chapter.id;
            deliverableCourseId = course.id;
            break;
          }
        }
        if (hasDeliverableQuestions) break;
      }

      const isAnswered = hasDeliverableQuestions && this.deliverableAnswers.some(
        (answer) => answer.moduleId === module.id && !answer.isDraft
      );

      return {
        module,
        isAnswered: hasDeliverableQuestions ? isAnswered : true,
        chapterId: deliverableChapterId,
        courseId: deliverableCourseId
      };
    }).filter((status) => status.chapterId);

    this.isAllAnswered = this.moduleStatuses.every((status) => status.isAnswered);
  }

  goToModule(status: ModuleStatus): void {
    if (status.chapterId && status.courseId) {
      const masterclassId = this.masterclass?.id;
      this.router.navigate([
        '/e-learning/masterclasses',
        masterclassId,
        'modules',
        status.module.id,
        status.chapterId,
        status.courseId
      ]);
    }
  }

  generateDeliverable(): void {
    if (!this.isAllAnswered || this.isGenerating || !this.masterclass) return;

    this.isGenerating = true;
    this.error = '';

    const payload = {
      userId: this.currentUser.id,
      masterclass: this.masterclass,
      imageStyle: this.selectedImageStyle
    };

    this.http.post<IGeneratedDeliverable>(`${environment.apiUrl}/deliverable/generate`, payload)
      .subscribe({
        next: (result) => {
          this.generatedDeliverable = {
            ...result,
            generatedAt: result.generatedAt ? new Date(result.generatedAt) : new Date()
          };
          this.isGenerating = false;

          if (result.status === 'completed') {
            this.router.navigate(['/e-learning/masterclasses', this.masterclassId, 'deliverable-result']);
          } else if (result.status === 'pending') {
            this.startPolling();
          }
        },
        error: (err) => {
          console.error('❌ ERREUR GÉNÉRATION LIVRABLE');
          if (err.error?.details) {
            console.error('📋 Détails:', JSON.stringify(err.error.details, null, 2));
            if (err.error.details.prompt) {
              console.error('📝 PROMPT ENVOYÉ:');
              console.error(err.error.details.prompt);
            }
            if (err.error.details.payload) {
              console.error('📦 PAYLOAD ENVOYÉ:');
              console.error(JSON.stringify(err.error.details.payload, null, 2));
            }
          } else {
            console.error('Erreur:', err);
          }
          this.error = 'Une erreur est survenue lors de la génération. Veuillez réessayer.';
          this.isGenerating = false;
        }
      });
  }

  get answeredCount(): number {
    return this.moduleStatuses.filter((s) => s.isAnswered).length;
  }

  get totalCount(): number {
    return this.moduleStatuses.length;
  }

  downloadPdf(): void {
    if (this.generatedDeliverable?.pdfUrl) {
      window.open(this.generatedDeliverable.pdfUrl, '_blank');
    }
  }

  copyGammaLink(): void {
    if (this.generatedDeliverable?.gammaUrl) {
      navigator.clipboard.writeText(this.generatedDeliverable.gammaUrl).then(() => {
        this.linkCopied = true;
        setTimeout(() => this.linkCopied = false, 3000);
      });
    }
  }

  private startPolling(): void {
    if (this.pollingInterval) return;

    this.pollingInterval = setInterval(() => {
      this.checkDeliverableStatus();
    }, 10000);
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private checkDeliverableStatus(): void {
    this.http.get<any>(`${environment.apiUrl}/deliverable/generated/${this.currentUser.id}/${this.masterclassId}`)
      .subscribe({
        next: (deliverable) => {
          if (deliverable && deliverable.status !== 'pending') {
            this.stopPolling();
            this.generatedDeliverable = {
              ...deliverable,
              generatedAt: new Date(deliverable.generatedAt)
            };

            if (deliverable.status === 'completed') {
              this.router.navigate(['/e-learning/masterclasses', this.masterclassId, 'deliverable-result']);
            }
          }
        },
        error: () => {}
      });
  }

  resetAndRetry(): void {
    this.http.delete(`${environment.apiUrl}/deliverable/reset/${this.currentUser.id}/${this.masterclassId}`)
      .subscribe({
        next: () => {
          this.generatedDeliverable = null;
          this.error = '';
        },
        error: (err) => {
          console.error('Error resetting deliverable:', err);
          this.error = 'Impossible de réinitialiser. Veuillez contacter un administrateur.';
        }
      });
  }
}
